import * as React from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import FlowSegmentedControl, { SegmentItem } from 'components/FlowSegmentedControl';
import classNames from 'styles/utils/classNames';
import { PresetType } from 'utils/shared/trades/presets';

interface Props {
  // The currently applied preset, or null when the filters match no named preset.
  activePreset: PresetType | null;
  onSelect: (preset: PresetType) => void;
  onClear: () => void;
  className?: string;
}

const PRESET_ITEMS: { preset: PresetType; label: string }[] = [
  { preset: PresetType.Unusual, label: 'Unusual' },
  { preset: PresetType.Sweeps, label: 'Sweeps' },
  { preset: PresetType.Momentum, label: 'Momentum' },
  { preset: PresetType.Moonshots, label: 'Moonshots' },
];

export default function FlowPresetSelector({ activePreset, onSelect, onClear, className }: Props) {
  const items: SegmentItem[] = PRESET_ITEMS.map(({ preset, label }) => ({
    value: String(preset),
    label,
  }));

  const hasActivePreset = activePreset !== null;

  // Always rendered so its slot never appears/disappears (which would shift the row);
  // disabled and dimmed when there is nothing to clear.
  const clearButton = (
    <button
      type="button"
      aria-label="Clear preset"
      disabled={!hasActivePreset}
      onClick={hasActivePreset ? onClear : undefined}
      className={classNames(
        'flex h-7 items-center border-l border-white/10 px-2 transition-colors',
        hasActivePreset
          ? 'text-gray-400 hover:bg-white/5 hover:text-white'
          : 'cursor-default text-gray-700',
      )}
    >
      <AiOutlineClose className="h-3 w-3" />
    </button>
  );

  return (
    <FlowSegmentedControl
      ariaLabel="Flow presets"
      tooltip="Presets · quick filters (click ✕ to clear)"
      className={className}
      items={items}
      isActive={(value) => activePreset === Number(value)}
      onSelect={(value) => {
        const preset = Number(value) as PresetType;
        // Clicking the already-active preset clears it (mirrors the explicit ✕).
        if (preset === activePreset) {
          onClear();
        } else {
          onSelect(preset);
        }
      }}
      trailing={clearButton}
    />
  );
}
